import { useState, useEffect } from 'react';
import { Save, Loader, AlertCircle, CheckCircle2, Image, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileUpload } from '../../components/cms/FileUpload';
import adminApi from '../../services/adminApi';
import type { GlobalSettings } from '../../types/cms';
import toast from 'react-hot-toast';

export default function GlobalSettingsPage() {
  const [logoFile, setLogoFile] = useState<File | string>('');
  const [faviconFile, setFaviconFile] = useState<File | string>('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [settings, setSettings] = useState<GlobalSettings & { id?: number }>({
    primaryColor: '#d4af37', // Theme color - sent to backend
    secondaryColor: '#8b0000', // Theme color - sent to backend
    contactEmail: '',
    contactPhone: '',
    restaurantName: '', // Maps to siteName in backend
    address: '', // Maps to contactAddress in backend
    description: '', // Maps to tagline in backend
    logoUrl: '',
    favicon: '',
    googleMapEmbedUrl: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    socialLinks: {
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: '',
      whatsapp: ''
    },
    openingHours: {
      weekdaysLabel: '', // Sent as flat field to backend
      weekdaysHours: '', // Sent as flat field to backend
      fridayLabel: '', // Sent as flat field to backend
      fridayHours: '' // Sent as flat field to backend
    },
    businessHours: { // UI only - not sent to backend
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '22:00', closed: false },
      saturday: { open: '09:00', close: '22:00', closed: false },
      sunday: { open: '09:00', close: '22:00', closed: false },
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | null; text: string }>({
    type: null,
    text: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async (preserveColors: boolean = false) => {
    setIsLoading(true);

    try {
      const response = await adminApi.fetch<GlobalSettings[]>('/globalSetting/getAll');
      
      if (response.length > 0) {
        setIsCreatingNew(false);
        const firstSettings = response[0];
        const settingsWithId = {
          ...firstSettings,
          id: typeof firstSettings.id === 'number' ? firstSettings.id : 1,
          restaurantName: firstSettings.siteName || '',
          address: firstSettings.contactAddress || '',
          description: firstSettings.tagline || '',
          // Colors: Backend doesn't store these, so use localStorage for persistence
          primaryColor: localStorage.getItem('theme_primaryColor') || (preserveColors && settings.primaryColor ? settings.primaryColor : '#d4af37'),
          secondaryColor: localStorage.getItem('theme_secondaryColor') || (preserveColors && settings.secondaryColor ? settings.secondaryColor : '#8b0000'),
          contactEmail: firstSettings.contactEmail || '',
          contactPhone: firstSettings.contactPhone || '',
          logoUrl: firstSettings.logoUrl || '',
          favicon: firstSettings.faviconUrl || '',
          metaTitle: firstSettings.metaTitle || '',
          metaDescription: firstSettings.metaDescription || '',
          metaKeywords: firstSettings.metaKeywords || '',
          googleMapEmbedUrl: firstSettings.googleMapEmbedUrl || '',
          socialLinks: {
            facebook: firstSettings.socialLinks?.facebook || '',
            instagram: firstSettings.socialLinks?.instagram || '',
            twitter: firstSettings.socialLinks?.twitter || '',
            youtube: firstSettings.socialLinks?.youtube || '',
            whatsapp: firstSettings.socialLinks?.whatsapp || ''
          },
          openingHours: {
            weekdaysLabel: firstSettings.weekdaysLabel || '',
            weekdaysHours: firstSettings.weekdaysHours || '',
            fridayLabel: firstSettings.fridayLabel || '',
            fridayHours: firstSettings.fridayHours || ''
          },
          businessHours: {
            monday: { open: '09:00', close: '22:00', closed: false },
            tuesday: { open: '09:00', close: '22:00', closed: false },
            wednesday: { open: '09:00', close: '22:00', closed: false },
            thursday: { open: '09:00', close: '22:00', closed: false },
            friday: { open: '09:00', close: '22:00', closed: false },
            saturday: { open: '09:00', close: '22:00', closed: false },
            sunday: { open: '09:00', close: '22:00', closed: false },
          }
        };

        setSettings(settingsWithId as GlobalSettings & { id: number });

        if (settingsWithId.logoUrl) {
          setLogoFile(settingsWithId.logoUrl);
        }
        if (settingsWithId.favicon) {
          setFaviconFile(settingsWithId.favicon);
        }
      } else {
        setIsCreatingNew(true);
        setMessage({ type: 'error', text: 'No settings found. Enter information and save to create.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load settings. Using defaults.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const formData = new FormData();
      
      if (logoFile instanceof File) {
        formData.append('logo', logoFile);
      }
      
      if (faviconFile instanceof File) {
        formData.append('favicon', faviconFile);
      }
      
      const dto = {
        siteName: settings.restaurantName,
        tagline: settings.description,
        contactEmail: settings.contactEmail,
        contactPhone: settings.contactPhone,
        contactAddress: settings.address,
        googleMapEmbedUrl: settings.googleMapEmbedUrl,
        primaryColor: settings.primaryColor,
        secondaryColor: settings.secondaryColor,
        weekdaysLabel: settings.openingHours?.weekdaysLabel || '',
        weekdaysHours: settings.openingHours?.weekdaysHours || '',
        fridayLabel: settings.openingHours?.fridayLabel || '',
        fridayHours: settings.openingHours?.fridayHours || '',
        metaTitle: settings.metaTitle,
        metaDescription: settings.metaDescription,
        metaKeywords: settings.metaKeywords,
        socialLinks: settings.socialLinks,
        ...(!(logoFile instanceof File) && settings.logoUrl && { logoUrl: settings.logoUrl }),
        ...(!(faviconFile instanceof File) && settings.favicon && { faviconUrl: settings.favicon })
      };
      
      const jsonBlob = new Blob([JSON.stringify(dto)], { type: 'application/json' });
      formData.append('globalSettingRequestDto', jsonBlob);
      
      if (isCreatingNew || !settings.id) {
        await adminApi.uploadFormData('/globalSetting', formData, 'POST');
        setIsCreatingNew(false);
      } else {
        await adminApi.uploadFormData(`/globalSetting/${settings.id}`, formData, 'PUT');
      }
      
      // Save colors to localStorage since backend doesn't store them
      if (settings.primaryColor) {
        localStorage.setItem('theme_primaryColor', settings.primaryColor);
      }
      if (settings.secondaryColor) {
        localStorage.setItem('theme_secondaryColor', settings.secondaryColor);
      }
      
      await loadSettings(true);
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      toast.success('Settings saved successfully!');
      setHasUnsavedChanges(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const updateField = (field: keyof GlobalSettings, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const updateSocialLink = (platform: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Global Settings</h1>
          <p className="text-muted-foreground">
            Manage your restaurant's global configuration and settings
          </p>
        </div>
      </div>

      {/* Unsaved Changes Warning Banner */}
      {hasUnsavedChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <Clock className="w-5 h-5 text-amber-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You have unsaved changes
                </p>
                <p className="text-xs text-amber-600">
                  Click Save Settings to persist your changes.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setHasUnsavedChanges(false);
                  loadSettings();
                }}
                className="text-amber-800 border-amber-300 hover:bg-amber-100"
              >
                Discard
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isSaving ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message.type && (
        <div className={`flex items-center p-4 rounded-lg animate-in ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}
        style={{ borderRadius: '12px' }}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Logo & Favicon Management */}
      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Image className="w-5 h-5 mr-2 text-[#d4af37]" />
            Branding Assets
          </CardTitle>
          <CardDescription>Upload and manage your restaurant's logo and favicon</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <div>
              <FileUpload
                value={logoFile}
                onChange={(file) => {
                  setLogoFile(file);
                  setHasUnsavedChanges(true);
                }}
                folder="branding"
                accept="image/*"
                maxSize={5}
                label="Restaurant Logo"
                description="Recommended: 200x200px"
                uploadOnSelect={false}
                variant="image"
                showPreview={true}
              />
            </div>

            {/* Favicon Upload */}
            <div>
              <FileUpload
                value={faviconFile}
                onChange={(file) => {
                  setFaviconFile(file);
                  setHasUnsavedChanges(true);
                }}
                folder="branding"
                accept="image/*,.ico"
                maxSize={1}
                label="Favicon"
                description="Recommended: 32x32px or 64x64px, ICO or PNG"
                uploadOnSelect={false}
                variant="image"
                showPreview={true}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card className="card-hover">
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>Essential restaurant details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="restaurantName" className="form-label">Restaurant Name</Label>
              <Input
                id="restaurantName"
                value={settings.restaurantName}
                onChange={(e) => updateField('restaurantName', e.target.value)}
                placeholder="Your Restaurant Name"
                className="form-input"
              />
            </div>
            <div>
              <Label htmlFor="contactEmail" className="form-label">Contact Email</Label>
              <Input
                id="contactEmail"
                type="email"
                value={settings.contactEmail}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                placeholder="info@restaurant.com"
                className="form-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contactPhone" className="form-label">Contact Phone</Label>
              <Input
                id="contactPhone"
                value={settings.contactPhone}
                onChange={(e) => updateField('contactPhone', e.target.value)}
                placeholder="+1 234 567 8900"
                className="form-input"
              />
            </div>
            <div>
              <Label htmlFor="address" className="form-label">Address</Label>
              <Input
                id="address"
                value={settings.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="123 Restaurant Street, Colombo"
                className="form-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="description" className="form-label">Description</Label>
            <textarea
              id="description"
              rows={3}
              value={settings.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="A brief description of your restaurant..."
              className="form-input w-full resize-none"
            />
          </div>
          <div>
            <Label htmlFor="googleMapEmbedUrl" className="form-label">Google Map Embed URL</Label>
            <Input
              id="googleMapEmbedUrl"
              value={settings.googleMapEmbedUrl || ''}
              onChange={(e) => updateField('googleMapEmbedUrl', e.target.value)}
              placeholder="https://www.google.com/maps/embed?pb=..."
              className="form-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Get the embed URL from Google Maps → Share → Embed a map
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Theme Colors - Disabled for now
      <Card className="card-hover">
        <CardHeader>
          <CardTitle>Theme Colors</CardTitle>
          <CardDescription>Customize your brand colors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="primaryColor" className="form-label">Primary Color</Label>
              <div className="flex items-center space-x-3">
                <Input
                  id="primaryColor"
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="w-20 h-12 p-1 form-input"
                />
                <Input
                  value={settings.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  placeholder="#d4af37"
                  className="form-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="secondaryColor" className="form-label">Secondary Color</Label>
              <div className="flex items-center space-x-3">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => updateField('secondaryColor', e.target.value)}
                  className="w-20 h-12 p-1 form-input"
                />
                <Input
                  value={settings.secondaryColor}
                  onChange={(e) => updateField('secondaryColor', e.target.value)}
                  placeholder="#8b0000"
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      */}

      {/* Social Links */}
      <Card className="card-hover">
        <CardHeader>
          <CardTitle>Social Media Links</CardTitle>
          <CardDescription>Connect your social media profiles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {['facebook', 'twitter', 'instagram', 'youtube'].map((platform) => (
              <div key={platform}>
                <Label htmlFor={platform} className="form-label capitalize">{platform}</Label>
                <Input
                  id={platform}
                  value={settings.socialLinks[platform] || ''}
                  onChange={(e) => updateSocialLink(platform, e.target.value)}
                  placeholder={`https://${platform}.com/yourpage`}
                  className="form-input"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Opening Hours */}
      <Card className="card-hover">
        <CardHeader>
          <CardTitle>Opening Hours</CardTitle>
          <CardDescription>Display your restaurant operating hours</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="weekdaysLabel" className="form-label">Weekdays Label</Label>
              <Input
                id="weekdaysLabel"
                value={settings.openingHours?.weekdaysLabel || ''}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    openingHours: { ...prev.openingHours, weekdaysLabel: e.target.value, weekdaysHours: prev.openingHours?.weekdaysHours || '' }
                  }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Monday - Thursday"
                className="form-input"
              />
            </div>
            <div>
              <Label htmlFor="weekdaysHours" className="form-label">Weekdays Hours</Label>
              <Input
                id="weekdaysHours"
                value={settings.openingHours?.weekdaysHours || ''}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    openingHours: { ...prev.openingHours, weekdaysLabel: prev.openingHours?.weekdaysLabel, weekdaysHours: e.target.value }
                  }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="09:00 AM - 10:00 PM"
                className="form-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fridayLabel" className="form-label">Weekend Label</Label>
              <Input
                id="fridayLabel"
                value={settings.openingHours?.fridayLabel || ''}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    openingHours: { ...prev.openingHours, weekdaysLabel: prev.openingHours?.weekdaysLabel, weekdaysHours: prev.openingHours?.weekdaysHours || '', fridayLabel: e.target.value }
                  }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Friday - Sunday"
                className="form-input"
              />
            </div>
            <div>
              <Label htmlFor="fridayHours" className="form-label">Weekend Hours</Label>
              <Input
                id="fridayHours"
                value={settings.openingHours?.fridayHours || ''}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    openingHours: { ...prev.openingHours, weekdaysLabel: prev.openingHours?.weekdaysLabel, weekdaysHours: prev.openingHours?.weekdaysHours || '', fridayLabel: prev.openingHours?.fridayLabel, fridayHours: e.target.value }
                  }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="09:00 AM - 11:00 PM"
                className="form-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Hours */}
      {/* <Card className="card-hover">
        <CardHeader>
          <CardTitle>Business Hours</CardTitle>
          <CardDescription>Set your operating hours for each day</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {days.map((day) => (
            <div key={day} className="flex items-center space-x-4">
              <div className="w-24">
                <Label className="form-label capitalize">{day}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!settings.businessHours[day]?.closed}
                  onChange={(e) => updateBusinessHours(day, 'closed', !e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Open</span>
              </div>
              {!settings.businessHours[day]?.closed && (
                <>
                  <Input
                    type="time"
                    value={settings.businessHours[day]?.open || '09:00'}
                    onChange={(e) => updateBusinessHours(day, 'open', e.target.value)}
                    className="form-input w-32"
                  />
                  <span className="text-sm text-gray-500">to</span>
                  <Input
                    type="time"
                    value={settings.businessHours[day]?.close || '22:00'}
                    onChange={(e) => updateBusinessHours(day, 'close', e.target.value)}
                    className="form-input w-32"
                  />
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card> */}

      {/* Save Settings Button - Bottom Right */}
      <div className="flex justify-end pt-4 border-t mt-6">
        <Button 
          onClick={handleSave} 
          disabled={isSaving || !hasUnsavedChanges} 
          variant="blueBg"
          className="gap-2"
        >
          {isSaving ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
